/**
 * 使い方ガイドの見た目（状態を持たない表示部品だけ）
 * ドット絵RPG風: 角丸なし、紺（app-quest-night）の太枠、クリーム地（#FFFCE8）、金（app-quest-gold）の選択カーソル。
 * 色は旧 VisitorTutorialModal と同じ値を使います（#FFFCE8 / #FFF4C2 / #FFF8DD / bg-app-quest-night）。
 */

import React, { useId } from 'react';
import { Pressable, Text, View } from '../../../shared/components/WebPrimitives';
import type { GuidedTourStep, GuidedTourStop } from '../utils/visitorGuidedTourPlan';
import type { OverlayLayout, Rect } from '../utils/visitorGuidedTourLayout';

/** 暗幕の色（紺寄りの黒。既存Modalの bg-black/60〜70 と同じ濃さ） */
const DIM_COLOR = 'rgba(10, 18, 34, 0.64)';

/**
 * ガイドの重なり順
 * 画面（ScreenLayout）は isolation: isolate で中の重なり（カート・トースト）を閉じるため、ガイドは画面全体より上に出る。
 * Modal（body直下の10000）より下で、Modalが開くとガイドは#rootごと隠れる。
 */
const GUIDED_TOUR_Z_INDEX = 9000;

/** 初回測定前のカードの位置（下部タブのおおよその高さの上） */
const DEFAULT_CARD_STYLE = Object.freeze({ left: 16, right: 16, bottom: 88 });

/**
 * 長方形を fixed 配置のstyleへ変換する
 * @param {Rect} rect - 長方形
 * @param {number} zIndex - 重なり順
 * @returns {React.CSSProperties} style
 */
const toFixedStyle = (rect: Rect, zIndex: number): React.CSSProperties => ({
  position: 'fixed',
  top: rect.top,
  left: rect.left,
  width: rect.width,
  height: rect.height,
  zIndex,
});

/** 説明カードのプロパティ */
type GuidedTourCardProps = Readonly<{
  step: GuidedTourStep;
  stops: readonly GuidedTourStop[];
  /** 「画面を準備しています…」「画面を移動しています…」。空なら出さない */
  statusText: string;
  /** 初回測定前はnull（その間は既定位置で描画する） */
  layout: OverlayLayout['card'] | null;
  /** 画面の移動中は次へ・戻るを押せなくする（連打で手順が飛ばないように） */
  isBusy: boolean;
  onNext: () => void;
  /** 先頭の手順ではnull（戻るボタンを出さない） */
  onBack: (() => void) | null;
  onSkip: () => void;
  cardRef: React.Ref<HTMLDivElement>;
  bodyRef: React.Ref<HTMLDivElement>;
  nextButtonRef: React.Ref<HTMLButtonElement>;
}>;

/**
 * 説明カード
 * - 下部タブは操作できるため非モーダルのdialog。focusは次へボタンに置いたままにし、手順の文言は読み上げ領域で伝える
 * - Escape（カードの中にfocusがあるとき）でスキップ。Modalが開いている間は#rootごとinertなので反応しない
 * - 上から: 紺の帯（帯文字 / スキップ）→ 立ち寄り先の目盛り → 本文（はみ出したらこの部分だけスクロール）→ 戻る / 次へ
 * - 次へは右側で幅2/3、48px以上。カードは下端を基準に置くため、文字が大きくなっても次へは同じ位置に残る
 * @param {GuidedTourCardProps} props - プロパティ
 * @returns {JSX.Element} 説明カード
 */
export const GuidedTourCard = ({
  step,
  stops,
  statusText,
  layout,
  isBusy,
  onNext,
  onBack,
  onSkip,
  cardRef,
  bodyRef,
  nextButtonRef,
}: GuidedTourCardProps): React.JSX.Element => {
  const titleId = useId();
  const bodyId = useId();
  const positionStyle: React.CSSProperties = layout
    ? { left: layout.left, width: layout.width, maxHeight: layout.maxHeight, bottom: layout.bottom }
    : DEFAULT_CARD_STYLE;

  /**
   * Escapeでスキップする
   * @param {React.KeyboardEvent} event - キーボードイベント
   * @returns {void}
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onSkip();
    }
  };

  return (
    <View
      aria-label="使い方ガイド"
      aria-modal="false"
      className="fixed flex flex-col overflow-hidden rounded-none border-4 border-app-quest-night bg-[#FFFCE8] shadow-[4px_4px_0_rgba(22,36,58,0.38)]"
      onKeyDown={handleKeyDown}
      ref={cardRef}
      role="dialog"
      style={{ ...positionStyle, position: 'fixed', zIndex: GUIDED_TOUR_Z_INDEX + 2 }}
    >
      <View className="shrink-0 flex-row items-center justify-between gap-2 bg-app-quest-night px-3">
        <Text className="text-[11px] font-bold tracking-widest text-[#FFF4C2]">{step.badge}</Text>
        <Pressable
          accessibilityLabel="ガイドをスキップ"
          accessibilityRole="button"
          className="min-h-[44px] justify-center px-1"
          onPress={onSkip}
        >
          <Text className="border border-[#FFF4C2]/60 px-2 py-0.5 text-xs font-semibold text-[#FFF4C2]">スキップ</Text>
        </Pressable>
      </View>

      <View accessible={false} className="shrink-0 flex-row gap-1 px-3 pt-2">
        {stops.map((stop, stopIndex) => {
          const isDone = step.progress.kind === 'goal'
            || (step.progress.kind === 'stop' && stopIndex < step.progress.stopIndex);
          const isCurrent = step.progress.kind === 'stop' && stopIndex === step.progress.stopIndex;
          return (
            <View key={stop.route} className="min-w-0 flex-1">
              <View
                className={`h-1.5 ${isDone || isCurrent ? 'bg-app-quest-gold' : 'bg-app-quest-night/20'} ${isCurrent ? 'outline outline-1 outline-app-quest-night' : ''}`}
              />
              {/* 高さの低い画面では名前を省いて目盛りだけにし、本文の行を優先する（現在地は帯の「ホーム 1/3」で分かる） */}
              <Text
                className={`mt-0.5 truncate text-center text-[10px] leading-4 [@media(max-height:600px)]:hidden ${isCurrent ? 'font-extrabold text-app-quest-night' : 'text-app-text-secondary'}`}
              >
                {stop.label}
              </Text>
            </View>
          );
        })}
      </View>

      <View className="min-h-0 flex-1 overflow-y-auto px-4 pb-2 pt-1" ref={bodyRef}>
        <Text aria-level={2} className="text-base font-extrabold leading-6 text-app-quest-night" id={titleId} role="heading">
          {step.title}
        </Text>
        <Text className="mt-1 block text-sm leading-6 text-app-text" id={bodyId}>
          {step.body}
        </Text>
        {step.hint ? (
          <View className="mt-2 border border-app-quest-night/30 bg-[#FFF8DD] px-3 py-2">
            <Text className="text-xs leading-5 text-app-quest-night">{step.hint}</Text>
          </View>
        ) : null}
        {statusText ? (
          <Text className="mt-1 block text-xs font-semibold text-app-text-secondary">{statusText}</Text>
        ) : null}
        <Text accessibilityLiveRegion="polite" accessibilityRole="status" className="sr-only">
          {`${step.badge}。${step.title}。${step.body}${step.moveHint ? `。${step.moveHint}` : ''}${statusText ? `。${statusText}` : ''}`}
        </Text>
      </View>

      {/* 次の画面へは来場者自身にタブを押してもらうのが本題なので、本文がスクロールしても隠れない位置に固定する */}
      {step.moveHint ? (
        <View className="mx-3 shrink-0 border-l-4 border-app-quest-gold bg-[#FFF4C2] px-3 py-1.5">
          <Text className="text-xs font-extrabold leading-5 text-app-quest-night">▼ {step.moveHint}</Text>
        </View>
      ) : null}

      <View className="shrink-0 flex-row gap-2 border-t-2 border-app-quest-night/20 px-3 pb-3 pt-2">
        {onBack ? (
          <Pressable
            accessibilityLabel="前の説明へ戻る"
            accessibilityRole="button"
            className="min-h-12 flex-1 items-center justify-center border-2 border-app-quest-night bg-[#FFF8DD] disabled:opacity-50"
            disabled={isBusy}
            onPress={onBack}
          >
            <Text className="text-sm font-bold text-app-quest-night">← 戻る</Text>
          </Pressable>
        ) : null}
        <Pressable
          accessibilityRole="button"
          aria-describedby={`${titleId} ${bodyId}`}
          className="min-h-12 flex-[2] items-center justify-center border-2 border-app-quest-night bg-app-quest-night px-2 disabled:opacity-60"
          disabled={isBusy}
          onPress={onNext}
          ref={nextButtonRef}
        >
          <Text className="text-base font-bold text-[#FFF4C2]">{step.nextLabel}</Text>
        </Pressable>
      </View>
    </View>
  );
};

/** 暗幕と穴のプロパティ */
type GuidedTourSpotlightProps = Readonly<{
  /** 初回測定前はnull（その間は画面全体を1枚で覆う） */
  layout: OverlayLayout | null;
  /** 穴のタップを実物へ通すか（手順の tapThrough）。falseなら透明な受け止め板を穴にも置く */
  tapThrough: boolean;
}>;

/**
 * 暗幕（タップを受け止める長方形）と、目印を囲む金の選択カーソル、次に押すタブの枠
 * 暗い部分 = タップが効かない部分。下部タブの上端より下は覆わない（タブは明るく、押すとガイドがその画面へ付いていく）。
 * 読み上げはカードが担うため、全体を読み上げ対象から外す。
 * @param {GuidedTourSpotlightProps} props - プロパティ
 * @returns {JSX.Element} 暗幕と穴
 */
export const GuidedTourSpotlight = ({ layout, tapThrough }: GuidedTourSpotlightProps): React.JSX.Element => {
  const blockers = layout?.blockers ?? [];
  const hole = layout?.hole ?? null;

  return (
    <View accessible={false}>
      {layout ? null : (
        <View className="fixed inset-0 touch-none" style={{ background: DIM_COLOR, zIndex: GUIDED_TOUR_Z_INDEX }} />
      )}
      {blockers.map((blocker) => (
        <View
          key={`${blocker.top}-${blocker.left}-${blocker.width}-${blocker.height}`}
          className="touch-none"
          style={{ ...toFixedStyle(blocker, GUIDED_TOUR_Z_INDEX), background: DIM_COLOR }}
        />
      ))}
      {hole && !tapThrough ? <View className="touch-none" style={toFixedStyle(hole, GUIDED_TOUR_Z_INDEX)} /> : null}
      {hole ? (
        <View
          className="pointer-events-none border-[3px] border-app-quest-gold shadow-[inset_0_0_0_2px_rgb(26_45_77)] motion-safe:animate-[guided-tour-cursor_1s_steps(2,end)_infinite]"
          style={toFixedStyle(hole, GUIDED_TOUR_Z_INDEX + 1)}
        >
          <Text
            className={`absolute left-1/2 -translate-x-1/2 bg-app-quest-gold px-1 text-[10px] leading-3 text-app-quest-night ${hole.markerEdge === 'bottom' ? '-bottom-3' : '-top-3'}`}
          >
            {hole.markerEdge === 'bottom' ? '▼' : '▲'}
          </Text>
        </View>
      ) : null}
      {layout?.tabRing ? (
        <View
          className="pointer-events-none border-[3px] border-app-quest-gold shadow-[0_0_0_2px_rgb(26_45_77)] motion-safe:animate-[guided-tour-cursor_1s_steps(2,end)_infinite]"
          style={toFixedStyle(layout.tabRing, GUIDED_TOUR_Z_INDEX + 1)}
        />
      ) : null}
    </View>
  );
};

/** 寄り道中の帯のプロパティ */
type GuidedTourDetourBarProps = Readonly<{
  /** 戻る先の表示名（例: 'QRスキャン'） */
  stopLabel: string;
  onResume: () => void;
  onSkip: () => void;
  /** viewport下端からの距離（初回測定前はnull） */
  bottom: number | null;
}>;

/**
 * 寄り道中（立ち寄り先ではない画面にいる間）の小さな帯
 * 暗幕を出さず、画面はそのまま使える。終了はスキップと同じ（既読にする）。
 * @param {GuidedTourDetourBarProps} props - プロパティ
 * @returns {JSX.Element} 帯
 */
export const GuidedTourDetourBar = ({ stopLabel, onResume, onSkip, bottom }: GuidedTourDetourBarProps): React.JSX.Element => (
  <View
    aria-label="使い方ガイドの途中"
    className="fixed left-4 right-4 mx-auto max-w-[480px] flex-row items-center gap-2 rounded-none border-4 border-app-quest-night bg-[#FFF4C2] px-3 py-2 shadow-[4px_4px_0_rgba(22,36,58,0.38)]"
    role="region"
    style={{ bottom: bottom ?? DEFAULT_CARD_STYLE.bottom, zIndex: GUIDED_TOUR_Z_INDEX + 2 }}
  >
    <Text className="min-w-0 flex-1 text-xs font-bold leading-5 text-app-quest-night">ガイドの途中です</Text>
    <Pressable
      accessibilityRole="button"
      className="min-h-[44px] items-center justify-center border-2 border-app-quest-night bg-app-quest-night px-3"
      onPress={onResume}
    >
      <Text className="text-sm font-bold text-[#FFF4C2]">{`${stopLabel}へ戻る`}</Text>
    </Pressable>
    <Pressable
      accessibilityLabel="ガイドを終了"
      accessibilityRole="button"
      className="min-h-[44px] items-center justify-center border-2 border-app-quest-night bg-[#FFF8DD] px-3"
      onPress={onSkip}
    >
      <Text className="text-sm font-bold text-app-quest-night">終了</Text>
    </Pressable>
  </View>
);
